import { ActionIcon, Center, Drawer, Group, Loader, Pagination, Stack, Text, Tooltip } from '@mantine/core';
import { useInterval, useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconRefresh } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImportTaskCard } from '@/pages/NewAdminConsole/components/ImportTaskCard';
import { usePermissionData } from '@/pages/NewAdminConsole/hooks/usePermissionData';
import { permissionApi, type UserImportTask } from '@/pages/NewAdminConsole/services/permissionApi';

interface Props {
  opened: boolean;
  onClose: () => void;
}

const PAGE_SIZE = 10;
const POLL_INTERVAL = 5000;

export const ImportTasksDrawer = ({ opened, onClose }: Props) => {
  const { t } = useTranslation();
  const { loadMembersData } = usePermissionData();
  const compact = useMediaQuery('(max-height: 500px)');

  const [tasks, setTasks] = useState<UserImportTask[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // 记录上一次轮询时每个任务的 status，用于侦测 PROCESSING/READY → FINISHED 跃迁
  const prevStatusRef = useRef<Map<number, UserImportTask['status']>>(new Map());
  // 避免 fetch 回调在 re-render 时造成轮询器抖动，用 ref 维持最新函数
  const fetchRef = useRef<() => Promise<void>>(async () => undefined);

  const hasProcessing = tasks.some((task) => task.status !== 'FINISHED');

  const fetchTasks = useCallback(
    async (page: number, { silent = false }: { silent?: boolean } = {}) => {
      if (!silent) setLoading(true);
      try {
        const response = await permissionApi.searchImportTasks({
          currentPage: page,
          pageSize: PAGE_SIZE,
          orderBys: ['CREATED_AT_DESC'],
        });

        if (!response.success) throw (response as any).error;

        const nextTasks = response.userImports ?? [];

        // 侦测 FINISHED 跃迁，触发一次成员列表刷新
        const prev = prevStatusRef.current;
        const justFinished = nextTasks.some(
          (task) => task.status === 'FINISHED' && prev.has(task.id) && prev.get(task.id) !== 'FINISHED'
        );
        const nextMap = new Map<number, UserImportTask['status']>();
        nextTasks.forEach((task) => nextMap.set(task.id, task.status));
        prevStatusRef.current = nextMap;

        setTasks(nextTasks);
        setTotalPages(Math.max(1, response.paginator?.pages ?? 1));

        if (justFinished) {
          loadMembersData(true);
        }
      } catch (error: any) {
        console.error('Fetch import tasks error:', error);
        const translationError = t(`adminConsole.tip.${error?.name}`);
        notifications.show({
          message:
            error?.name && translationError !== `adminConsole.tip.${error.name}`
              ? translationError
              : t('adminConsole.newConsole.import.tasks.loadFailed'),
          color: 'red',
        });
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [loadMembersData, t]
  );

  useEffect(() => {
    fetchRef.current = () => fetchTasks(currentPage, { silent: true });
  }, [fetchTasks, currentPage]);

  const pollInterval = useInterval(() => {
    if (hasProcessing) fetchRef.current();
  }, POLL_INTERVAL);

  // Drawer 打开时首次加载 + 启动轮询；关闭时停轮询、清数据
  useEffect(() => {
    if (opened) {
      setCurrentPage(1);
      prevStatusRef.current = new Map();
      fetchTasks(1);
      pollInterval.start();
      return () => pollInterval.stop();
    }
    pollInterval.stop();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // 切换分页时重新加载
  useEffect(() => {
    if (!opened) return;
    fetchTasks(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={compact ? '100%' : 'md'}
      title={
        <Group gap="xs">
          <Text fw={600}>{t('adminConsole.newConsole.import.tasks.title')}</Text>
          <Tooltip label={t('adminConsole.newConsole.import.tasks.refresh')} withArrow>
            <ActionIcon
              variant="subtle"
              onClick={() => fetchTasks(currentPage)}
              aria-label={t('adminConsole.newConsole.import.tasks.refresh')}
              loading={loading}
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      }
    >
      <Stack gap="sm" style={{ minHeight: 200 }}>
        {loading && tasks.length === 0 ? (
          <Center h={200}>
            <Loader size="sm" />
          </Center>
        ) : tasks.length === 0 ? (
          <Center h={200}>
            <Text size="sm" c="dimmed">
              {t('adminConsole.newConsole.import.tasks.empty')}
            </Text>
          </Center>
        ) : (
          <>
            {tasks.map((task) => (
              <ImportTaskCard key={task.id} task={task} compact={compact} />
            ))}
            {totalPages > 1 && (
              <Group justify="center" mt="sm">
                <Pagination value={currentPage} onChange={setCurrentPage} total={totalPages} size="sm" />
              </Group>
            )}
          </>
        )}
      </Stack>
    </Drawer>
  );
};
